import { cloneElement, type ReactElement, type ReactNode } from 'react';
import { Image, type ImageProps } from 'expo-image';
import { IconHome, type IconProps } from '@tabler/icons-react-native';
import type { DimensionValue } from 'react-native';

import { Box, Text, useTheme, type Theme } from 'app/shopify/theme';

import { ThumbnailFrame, type ThumbnailImageSize, type ThumbnailShape } from './ThumbnailFrame';

export type { ThumbnailImageSize, ThumbnailShape } from './ThumbnailFrame';
export type ThumbnailCounterSize = 24 | 32 | 40 | 48 | 64 | 120;
export type ThumbnailIconSize = 24 | 32 | 40 | 48 | 64;
export type ThumbnailBackground =
  | 'brand-alt'
  | 'neutral'
  | 'brand-secondary-1'
  | 'brand-secondary-2'
  | 'brand-secondary-3'
  | 'brand-secondary-4'
  | 'brand-secondary-5';

interface ThumbnailBaseProps {
  shape?: ThumbnailShape;
  /** Omit for decorative thumbnails; describe meaningful images and icons. */
  accessibilityLabel?: string;
  testID?: string;
}

export type ThumbnailImageProps = ThumbnailBaseProps & {
  element?: 'image';
  source?: ImageProps['source'];
  /** Custom media fills the image slot; source is used when omitted. */
  image?: ReactNode;
  border?: boolean;
} & ({ size?: ThumbnailImageSize; width?: never } | { size: 'free'; width?: DimensionValue });

export type ThumbnailCounterProps = ThumbnailBaseProps & {
  element: 'counter';
  size?: ThumbnailCounterSize;
  background?: 'brand-alt' | 'neutral';
  /** Display text is caller-owned, including any leading + or localized formatting. */
  count: string | number;
};

export type ThumbnailIconProps = ThumbnailBaseProps & {
  element: 'icon';
  intensity?: 'strong' | 'subtle';
  icon?: ReactElement<IconProps>;
} & ({ size?: ThumbnailIconSize; background?: ThumbnailBackground } | { size: 88; background: 'neutral' });

/** The Figma family is presentational. Compose a button around it when activation is needed. */
export type ThumbnailProps = ThumbnailImageProps | ThumbnailCounterProps | ThumbnailIconProps;

const COUNTER_TYPOGRAPHY = {
  24: 'captionBaseStrong',
  32: 'captionLargeStrong',
  40: 'bodyXSmallStrong',
  48: 'bodySmallStrong',
  64: 'bodyBaseStrong',
  120: 'headerMedium',
} as const;

const ICON_SIZE_TOKENS = {
  24: 'sizing.general.x3',
  32: 'sizing.general.x4',
  40: 'sizing.general.x5',
  48: 'sizing.general.x6',
  64: 'sizing.general.x8',
  88: 'sizing.general.x14',
} as const;

function backgroundToken(background: ThumbnailBackground, intensity: 'strong' | 'subtle'): keyof Theme['colors'] {
  if (background === 'brand-alt') return intensity === 'strong' ? 'surface.secondary' : 'surface.primary';
  if (background === 'neutral') return intensity === 'strong' ? 'surface.inverse.default' : 'surface.secondary-neutral';
  const secondary = background.replace('brand-', '') as `secondary-${1 | 2 | 3 | 4 | 5}`;
  return `surface.brand.${secondary}.${intensity === 'strong' ? 'default' : 'subtle'}`;
}

export function Thumbnail(props: ThumbnailProps) {
  const theme = useTheme();
  const { shape = 'square', accessibilityLabel, testID } = props;

  if (props.element === 'counter') {
    const { size = 64, background = 'brand-alt', count } = props;
    return (
      <ThumbnailFrame
        size={size}
        shape={shape}
        media={false}
        backgroundColor={backgroundToken(background, 'strong')}
        accessibilityLabel={accessibilityLabel ?? String(count)}
        accessibilityRole="text"
        testID={testID}
      >
        <Text
          accessible={false}
          variant={COUNTER_TYPOGRAPHY[size]}
          color={background === 'neutral' ? 'text.inverse.primary' : 'text.primary'}
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.5}
          textAlign="center"
          maxWidth="100%"
          style={{ includeFontPadding: false }}
        >
          {count}
        </Text>
      </ThumbnailFrame>
    );
  }

  if (props.element === 'icon') {
    const { size = 64, background = 'brand-alt', intensity = 'strong' } = props;
    const icon = props.icon ?? <IconHome />;
    const color =
      intensity === 'subtle' || background === 'brand-alt'
        ? 'icon.primary'
        : background === 'brand-secondary-2'
          ? 'black'
          : 'icon.inverse.primary';
    return (
      <ThumbnailFrame
        size={size}
        shape={shape}
        media={false}
        backgroundColor={backgroundToken(background, intensity)}
        accessibilityLabel={accessibilityLabel}
        testID={testID}
      >
        {cloneElement(icon, {
          size: Number(theme.tokens.sizing[ICON_SIZE_TOKENS[size]]),
          color: theme.colors[color],
          stroke: 2,
          accessible: false,
        })}
      </ThumbnailFrame>
    );
  }

  const { size = 64, border = true, source = require('./assets/image-placeholder.png'), image, width } = props;
  return (
    <ThumbnailFrame
      size={size}
      shape={shape}
      media
      border={border}
      width={width}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
    >
      <Box
        width="100%"
        height="100%"
        accessible={false}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        {image ?? (
          <Image source={source} contentFit="cover" accessible={false} style={{ width: '100%', height: '100%' }} />
        )}
      </Box>
    </ThumbnailFrame>
  );
}
